import { graphql } from 'react-relay';
import set from 'lodash/set';

import { PlatformSiteAri } from '@atlassian/ari';

import { CONTEXT_PATH } from '@confluence/named-routes/entry-points/context.ts';
import { expVal, UNSAFE_expValNoExposure } from '@confluence/feature-experiments';
import { getIdFromConfluenceContentAri } from '@confluence/content-types-utils/entry-points/getIdFromConfluenceContentAri.ts';
import { isContentTypeEnabledInCurrentEnv } from '@confluence/content-types-utils/entry-points/isContentTypeEnabledInCurrentEnv.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloInteropMutableHelper } from '@confluence/apollo-relay-interop/entry-points/ApolloInteropMutableHelper.ts';

import { RecentContentMenuQuery } from './queries/RecentContentMenuQuery.graphql.ts';
import {
	ActivityObjectType,
	getAllRecentInitialVars,
	isThirdPartyActivity,
	objectTypes,
	PAGE_SIZE,
	WORKED_ON_EVENT_TYPES,
} from './recentQueryConstants.ts';
import type { RecentContentMenuQuery as RecentContentMenuQueryType } from './queries/__types__/RecentContentMenuQuery.ts';
import type { recentQueryConfigRecentlyCreatedByMeQuery$data } from './__generated__/recentQueryConfigRecentlyCreatedByMeQuery.graphql.ts';

export type FilterType = 'ALL' | 'COLLABORATED_ON' | 'WORKED_ON' | 'CREATED_BY_ME';

type ActivitiesNode = ApolloInteropMutableHelper<
	NonNullable<
		NonNullable<NonNullable<RecentContentMenuQueryType['activity']>['myActivity']>['all']['edges']
	>[number]
>;

// Shared shape produced by `transformActivityData` and (optionally url-augmented by)
// `transformActivityNodeLegacyUrl`. The original Relay `ActivityNode`'s `object.data` is left in
// place and a sibling `object.content` field is added, holding the V3 -> V2 widened payload.
// `content` is intentionally `any` because the transform mutates strict GraphQL enum values to
// lowercase strings consumed by legacy V2 callers (see comment in `transformActivityData`).
// This whole shape collapses back to the Relay type in DISCO-4281.
export type TransformedActivityNode = Omit<ActivitiesNode['node'], 'id' | 'object'> & {
	id: string | undefined;
	timestamp: ActivitiesNode['node']['event']['timestamp'] | undefined;
	object: ActivitiesNode['node']['object'] & {
		content: any;
		url?: string;
	};
};

const transformActivityNodeLegacyUrl = (
	activitiesNode: ActivitiesNode,
): TransformedActivityNode => {
	const transformedNode = transformActivityData(activitiesNode?.node);
	if (!transformedNode?.object?.content?.links?.webui) return transformedNode;

	return {
		...transformedNode,
		object: {
			...transformedNode.object,
			url: `${CONTEXT_PATH}${transformedNode.object.content.links.webui}`,
			content: {
				...transformedNode.object.content,
			},
		},
	};
};

const transformActivityData = (recent: ActivitiesNode['node']): TransformedActivityNode => {
	// 3P items link out to their provider and skip the Confluence-specific transform below.
	// Without a title, URL or provider there is nothing to show, so `content` is null and the node is dropped.
	if (isThirdPartyActivity(recent?.object?.id)) {
		const data = recent.object.data;
		const url = data?.url ?? undefined;

		return {
			...recent,
			id: recent.object.id,
			timestamp: recent.event?.timestamp,
			object: {
				...recent.object,
				url,
				content:
					data?.displayName && url && data.provider?.name
						? {
								id: recent.object.id,
								title: data.displayName,
								thirdPartyId: data.thirdPartyId,
								// The provider takes the space's place in the row, e.g. "Google Drive · 2 hours ago".
								// Provider names can end in " provider" (e.g. "Google Drive provider"), as create-space's
								// connectorFiles.ts also handles.
								space: { name: data.provider.name.replace(/\s+provider$/i, '') },
							}
						: null,
			},
		};
	}

	if (recent?.object?.data?.commentId) {
		recent.event.eventType = 'commented';
		// @ts-expect-error - FIXME: This error was surfaced after enabling the `typescriptExcludeUndefinedFromNullableUnion`
		// Relay compiler option. Previously, `undefined` was incorrectly included in nullable unions in generated types; this option removes it. The owning team should fix this properly.
		recent.object.data = recent?.object?.data?.container;
	}

	// V3 -> V2 transformation widens strict GraphQL enum values (e.g. ConfluenceContentType,
	// ConfluenceContentStatus) to lowercase strings consumed by legacy V2 callers. The narrow
	// generated types reject these mutations, so we treat `content` as loosely typed here.
	// See TODO below — this whole block goes away in DISCO-4281.
	const content: any = recent?.object?.data;

	if (content) {
		content.type =
			content.type?.toLowerCase() === 'blog_post' ? 'blogpost' : content.type?.toLowerCase();
		content.subtype = content.subtype?.toLowerCase();

		// The "status" field is ConfluencePage/ConfluenceBlogPost type for pages/blogs but is
		// ConfluenceContentStatus type for all other content types. To prevent the GraphQL
		// "returns different types" error, we are using the aliases "pageStatus" and "blogStatus"
		content.status = [content.status, content.pageStatus, content.blogStatus]
			.find((status: string | null | undefined) => status)
			?.toLowerCase();

		// FE workaround for activity query returning "status: DRAFT" for published content with unpublished changes
		if (content.status === 'draft' && content.createdAt) {
			content.status = 'current';
		}
	}

	if (recent?.event) {
		recent.event.eventType = recent.event.eventType?.toUpperCase();
	}

	const transformedId =
		content?.id &&
		getIdFromConfluenceContentAri({
			ari: content.id,
			resourceType: content.type,
		});

	const recentData = recent?.object?.data;

	// TODO: This is currently transforming activity V3 data into activity V2 format. This
	// tranformation will be simplified in DISCO-4281
	const transformedContent = {
		...recentData,
		id: transformedId,
		metadata: {
			...recentData?.metadata,
			currentuser: {
				favourited: {
					isFavourite: recentData?.viewer?.favoritedSummary?.isFavorite,
				},
			},
		},
		links: {
			webui: recentData?.links?.webUi,
			editui: recentData?.links?.editUi,
		},
		subType: recentData?.subtype,
		emoji: {
			nodes: [
				{
					key: 'emoji-title-published',
					value: recentData?.metadata?.titleEmojiPublished?.value,
				},
				{
					key: 'emoji-title-draft',
					value: recentData?.metadata?.titleEmojiDraft?.value,
				},
			],
		},
	};

	return {
		...recent,
		id: transformedId,
		timestamp: recent?.event?.timestamp,
		object: {
			...recent?.object,
			content: transformedContent,
		},
	};
};

const mergeActivityMyActivityAllEdges = (prev, fetchMoreResult) => {
	const prevNodes = prev.activity?.myActivity?.all?.edges || [];
	const fetchMoreNodes = fetchMoreResult.activity?.myActivity?.all?.edges || [];
	const mergedNodes = [...prevNodes, ...fetchMoreNodes];
	const result = { ...fetchMoreResult };
	set(result, 'activity.myActivity.all.edges', mergedNodes);
	return result;
};

const getMyActivityAllPaginationParams = (data) => {
	const allActivity = data?.activity?.myActivity?.all;
	return {
		endCursor: allActivity?.pageInfo?.endCursor || '',
		hasNextPage: allActivity?.pageInfo?.hasNextPage || false,
	};
};

const getAllRecentQueryOptions = (cloudId) => ({
	query: RecentContentMenuQuery,
	// Read when the dialog renders, so this logs the 3P experiment exposure (the preloader doesn't)
	initialVars: getAllRecentInitialVars(
		cloudId,
		expVal('confluence_recent_3p_activities', 'isEnabled', false),
	),
	fetchMoreVars: (endCursor) => ({
		cloudId: PlatformSiteAri.create({ siteId: cloudId }).toString(),
		after: endCursor,
	}),
	transformNodes: (data: RecentContentMenuQueryType) => {
		const nodes = (data?.activity?.myActivity?.all?.edges || []).map(
			transformActivityNodeLegacyUrl,
		);
		// Only 3P items can lack content, but the filter is gated too, so Control stays as on master.
		// No exposure here: the dialog's initialVars logs it.
		return UNSAFE_expValNoExposure('confluence_recent_3p_activities', 'isEnabled', false)
			? nodes.filter((node) => node.object.content)
			: nodes;
	},
	getPaginationParams: getMyActivityAllPaginationParams,
	updatePaginationResults: mergeActivityMyActivityAllEdges,
});

const getCollaboratedOnQueryOptions = (cloudId) => ({
	query: RecentContentMenuQuery,
	initialVars: {
		cloudId: PlatformSiteAri.create({ siteId: cloudId }).toString(),
		after: '',
		first: PAGE_SIZE,
		eventTypes: WORKED_ON_EVENT_TYPES,
		objectTypes: [...objectTypes(), ActivityObjectType.COMMENT],
	},
	fetchMoreVars: (endCursor) => ({
		cloudId: PlatformSiteAri.create({ siteId: cloudId }).toString(),
		after: endCursor,
	}),
	transformNodes: (data: RecentContentMenuQueryType) =>
		(data?.activity?.myActivity?.all?.edges || []).map(transformActivityNodeLegacyUrl),
	getPaginationParams: getMyActivityAllPaginationParams,
	updatePaginationResults: mergeActivityMyActivityAllEdges,
});

const getWorkedOnQueryOptions = (cloudId) => ({
	query: RecentContentMenuQuery,
	initialVars: {
		cloudId: PlatformSiteAri.create({ siteId: cloudId }).toString(),
		after: '',
		first: PAGE_SIZE,
		eventTypes: WORKED_ON_EVENT_TYPES,
		objectTypes: objectTypes(),
	},
	fetchMoreVars: (endCursor) => ({
		cloudId: PlatformSiteAri.create({ siteId: cloudId }).toString(),
		after: endCursor,
	}),
	transformNodes: (data: RecentContentMenuQueryType) =>
		(data?.activity?.myActivity?.all?.edges || []).map(transformActivityNodeLegacyUrl),
	getPaginationParams: getMyActivityAllPaginationParams,
	updatePaginationResults: mergeActivityMyActivityAllEdges,
});

const mapCreatedByMeTabNodes = (node) => {
	return {
		...node,
		// Fall back to the content's creation date only when there's no `lastcontributed` value at all
		// - e.g. content was created with Rovo but hasn't been manually edited by the user.
		timestamp:
			node?.content?.metadata?.currentuser?.lastcontributed?.when ??
			node?.content?.metadata?.createdDate,
		object: {
			url: `${CONTEXT_PATH}${node.url}`,
			content: node.content,
		},
	};
};

const getCreatedByMeQueryOptions = () => {
	return {
		query: graphql`
			query recentQueryConfigRecentlyCreatedByMeQuery(
				$cql: String!
				$cqlcontext: String!
				$first: Int
				$after: String
			) {
				search: confluence_search(
					cql: $cql
					cqlcontext: $cqlcontext
					first: $first
					after: $after
				) {
					nodes {
						url
						content {
							id
							type
							subType
							title
							status
							space {
								id
								name
							}
							links {
								webui
								editui
							}
							metadata {
								createdDate
								currentuser {
									favourited {
										isFavourite
										favouritedDate
									}
									lastcontributed {
										status
										when
									}
								}
							}
							emoji: properties(keys: ["emoji-title-published", "emoji-title-draft"]) {
								nodes {
									id
									key
									value
								}
							}
							space {
								alias
							}
						}
					}
					pageInfo {
						hasNextPage
						endCursor
					}
				}
			}
		`,
		initialVars: {
			cql: `type in (page, blogpost, embed, whiteboard, database${isContentTypeEnabledInCurrentEnv('slide') ? ', slide' : ''}) and owner=currentUser() and created >= startOfDay("-90d") order by created desc`,
			cqlcontext: `{"contentStatuses":["draft", "current"]}`,
			after: '',
			first: PAGE_SIZE,
		},
		fetchMoreVars: (endCursor) => ({
			after: endCursor,
		}),
		transformNodes: (data: recentQueryConfigRecentlyCreatedByMeQuery$data) =>
			(data?.search?.nodes || []).map(mapCreatedByMeTabNodes),
		getPaginationParams: (data: recentQueryConfigRecentlyCreatedByMeQuery$data) => ({
			endCursor: data?.search?.pageInfo?.endCursor || '',
			hasNextPage: data?.search?.pageInfo?.hasNextPage || false,
		}),
		updatePaginationResults: (prev, fetchMoreResult) => {
			const prevNodes = prev.search?.nodes || [];
			const fetchMoreNodes = fetchMoreResult.search?.nodes || [];
			const mergedNodes = [...prevNodes, ...fetchMoreNodes];
			const result = { ...fetchMoreResult };
			set(result, 'search.nodes', mergedNodes);
			return result;
		},
	};
};

type QueryOptions = {
	query: any;
	initialVars: any;
	fetchMoreVars:
		| ((endCursor: string) => { cloudId: string; after: string })
		| ((endCursor: string) => { after: string });
	transformNodes?: any;
	getPaginationParams?: any;
	updatePaginationResults?: any;
};

export const getQueryOptions = (cloudId: string): Record<FilterType, QueryOptions> => {
	const queryOptions: Record<FilterType, QueryOptions> = {
		ALL: getAllRecentQueryOptions(cloudId),
		COLLABORATED_ON: getCollaboratedOnQueryOptions(cloudId),
		WORKED_ON: getWorkedOnQueryOptions(cloudId),
		CREATED_BY_ME: getCreatedByMeQueryOptions(),
	};
	return queryOptions;
};
